import React from 'react'
import { buildCloudinaryUrl, ContentData } from 'services/cloudinary'
import { View, ViewStyle, StyleSheet } from 'react-native'
import { color, spacing, fontSize, fontFamily } from '@partipost-cm-new/app-preview/dist/Styles/Base'
import { moderateScale } from '@partipost-cm-new/app-preview/dist/Utils/Scaling'
import Card from '@partipost-cm-new/app-preview/dist/Components/Card'
import PartiText from '@partipost-cm-new/app-preview/dist/Components/PartiText'
import { useNewTranslation } from 'hooks'
import Icon from 'particles/Icon'

export interface MediaState extends File, ContentData {
    image: string
    img_main: string
    resource_type: string
    public_id: string
    uuid: string
    preview?: string
}

interface MediaPreviewProps {
    files?: MediaState[]
    itemContainerWidth?: string | number
    itemContainerHeight?: string | number
    resizeMode?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down'
    caption?: string
    cardStyle?: ViewStyle
}

const MediaPreview: React.FC<MediaPreviewProps> = ({
    files = [],
    itemContainerWidth = moderateScale(108),
    itemContainerHeight = moderateScale(108),
    resizeMode = 'contain',
    caption,
    cardStyle,
}) => {
    const [t] = useNewTranslation()

    return (
        <Card cardStyle={{ ...styles.container, ...cardStyle }}>
            {(files && files.length > 0) || caption ? (
                <>
                    <PartiText style={styles.title}>
                        {t('appCampaign.joinCampaign.submitContent.photoExampleHeader')}
                    </PartiText>
                    {caption && <PartiText style={{ paddingBottom: 12 }}>{caption}</PartiText>}
                    {files.length > 0 && <PartiText style={styles.title}>{t('placeholderText.tapToExpand')}</PartiText>}
                    <View style={styles.mediaContainerStyle}>
                        {files?.map((file: MediaState, index: number) => (
                            <View
                                key={index}
                                style={{
                                    width: itemContainerWidth,
                                    height: itemContainerHeight,
                                    ...styles.fileContainer,
                                }}
                            >
                                {file?.resource_type === 'image' || file?.type?.startsWith('image') ? (
                                    <img
                                        alt={file.name}
                                        src={file.preview ? file.preview : buildCloudinaryUrl(file)}
                                        style={{ width: '100%', height: '100%', objectFit: resizeMode }}
                                    />
                                ) : file?.resource_type === 'video' || file?.type?.startsWith('video') ? (
                                    <video controls style={{ width: '100%', height: '100%', objectFit: resizeMode }}>
                                        <source src={file.preview ? file.preview : buildCloudinaryUrl(file)} />
                                    </video>
                                ) : null}
                            </View>
                        ))}
                    </View>
                </>
            ) : (
                <View style={styles.empty}>
                    <Icon size="36px" name="image" color="neutralAction" form="fill" />
                    <PartiText style={{ width: 230, textAlign: 'center' }}>
                        {t('placeholderText.imageUploader.submitTwentyPhotosVideos')}
                    </PartiText>
                </View>
            )}
        </Card>
    )
}

const styles = StyleSheet.create({
    container: {
        borderRadius: moderateScale(10),
        padding: spacing.medium,
        margin: 0,
        marginBottom: spacing.medium2,
        minHeight: moderateScale(120),
        justifyContent: 'center',
    },
    title: {
        fontSize: fontSize.extraSmall,
        paddingBottom: spacing.small2,
        color: color.tundora,
        fontFamily: fontFamily.copy,
    },
    mediaContainerStyle: {
        display: 'flex',
        flexDirection: 'row',
        gap: moderateScale(8),
        overflowY: 'hidden',
        overflowX: 'auto',
    },
    fileContainer: {
        backgroundColor: color.white,
        display: 'flex',
        justifyContent: 'center',
    },
    empty: {
        display: 'flex',
        alignItems: 'center',
        gap: moderateScale(12),
    },
})

export default MediaPreview
